import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardTitle } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/keys.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';

interface DetailResp {
  profile: {
    id: string;
    first_name: string;
    last_name: string;
    country: string | null;
    status: 'active' | 'suspended' | 'closed';
    has_completed_kyc: boolean;
  };
  kyc: Array<{ id: string; status: string; created_at: string }>;
  transfers: Array<{
    id: string;
    reference_code: string;
    status: string;
    amount_send: number;
    from_currency: string;
    created_at: string;
  }>;
}

export function CustomerDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: id ? qk.customer(id) : ['noop'],
    queryFn: () => callFunction<DetailResp>('admin-users-get', { query: { id: id! } }),
    enabled: !!id,
  });

  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'suspended') =>
      callFunction('admin-users-set-status', { body: { user_id: id, status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: id ? qk.customer(id) : ['noop'] }),
  });

  if (detail.isLoading) return <LoadingScreen />;
  if (detail.isError || !detail.data) return <ErrorState onRetry={() => detail.refetch()} />;
  const { profile, kyc, transfers } = detail.data;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {t('customers.detailTitle', { name: `${profile.first_name} ${profile.last_name}` })}
          </CardTitle>
        </CardHeader>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-text-muted">{t('customers.country')}</dt>
          <dd>{profile.country ?? '—'}</dd>
          <dt className="text-text-muted">{t('customers.kyc')}</dt>
          <dd>{profile.has_completed_kyc ? t('common.yes') : t('common.no')}</dd>
          <dt className="text-text-muted">{t('customers.status')}</dt>
          <dd>{profile.status}</dd>
        </dl>
        <div className="mt-4 flex gap-2">
          {profile.status === 'active' ? (
            <Button variant="danger" onClick={() => setStatus.mutate('suspended')}>
              {t('customers.suspend')}
            </Button>
          ) : (
            <Button onClick={() => setStatus.mutate('active')}>{t('customers.reactivate')}</Button>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('customers.tabs.transfers')}</CardTitle>
        </CardHeader>
        <ul className="flex flex-col gap-1 text-sm">
          {transfers.map((tr) => (
            <li key={tr.id} className="flex justify-between border-b border-border py-2">
              <span className="font-mono">{tr.reference_code}</span>
              <span>
                {tr.amount_send} {tr.from_currency}
              </span>
              <span className="text-text-muted">{tr.status}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('customers.tabs.kyc')}</CardTitle>
        </CardHeader>
        <ul className="flex flex-col gap-1 text-sm">
          {kyc.map((k) => (
            <li key={k.id} className="flex justify-between border-b border-border py-2">
              <span>{k.id}</span>
              <span className="text-text-muted">{k.status}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
